import * as React from "react";
import { useCart } from "../context/CartContext.tsx";
import { useAuth } from "../context/AuthContext.tsx";
import CircularProgress from "@mui/material/CircularProgress";
import SuccessAlert from "./SuccessAlert.tsx";
import FailAlert from "./FailAlert.tsx";
import SellerAlert from "./SellerAlert.tsx";
import {
  enviar_ped_ftp,
  reservar_sequencia_pedidos,
  enviar_ped_db,
} from "../services/peds-api.js";
import {
  obterDataAtual,
  obterHoraAtual,
  obterDataFormatadaParaLinhaP,
  obterHoraCompleta,
} from "../util/date-format.js";
import {
  Close,
  ShoppingBagOutlined,
  Add,
  Remove,
  ShoppingBagSharp,
  ShoppingCart,
} from "@mui/icons-material";
import {
  Drawer,
  Box,
  Button,
  Typography,
  Divider,
  IconButton,
  Stack,
  FormControl,
  TextField,
  FormLabel,
} from "@mui/material";

const CartDrawer = ({ open, onClose }) => {
  const { userData } = useAuth();
  const {
    cartItems,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    clearCart,
  } = useCart();

  const [quantities, setQuantities] = React.useState({});
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [failAlertOpen, setFailAlertOpen] = React.useState(false);
  const [loadingCart, setLoadingCart] = React.useState(false);
  const [observacao, setObservacao] = React.useState("");
  const [sellerAlertOpen, setSellerAlertOpen] = React.useState(false);

  React.useEffect(() => {
    const initialQuantities = {};
    cartItems.forEach((item) => {
      if (!quantities[item.descricao]) {
        initialQuantities[item.descricao] = 1;
      }
    });

    setQuantities((prev) => ({ ...initialQuantities, ...prev }));
    // eslint-disable-next-line
  }, [cartItems]);

  const handleRemoveCart = (id) => {
    removeFromCart(id);
  };

  const calcularTotal = (items) => {
    return items
      .reduce((acc, item) => {
        // return acc + item.preco * (item.quantidade_item || 1);
        return acc + item.valorX * (item.quantidade_item || 1);
      }, 0)
      .toFixed(2);
  };

  const calcularQtdItens = () => {
    return cartItems.reduce((acc, item) => acc + item.quantidade_item, 0);
  };

  const enviarPedido = async (visita_cliente) => {
    setLoadingCart(true);
    const cod_repre = userData?.reprE_CODIGO;
    const sequenciaObtida = await reservar_sequencia_pedidos();

    const momento_pedido = new Date();

    // formata o arquivo para ftp
    const nome_ped = formataNomeArquivo(cod_repre, sequenciaObtida);
    const linhaP = formataLinhaP(sequenciaObtida, momento_pedido, visita_cliente);
    const linhasI = formataLinhaI(sequenciaObtida);
    const linhaV = formataLinhaV(sequenciaObtida, momento_pedido);
    const linhasParciais = [linhaP, ...linhasI, linhaV]; // feitos para somar e calcular linhaX
    const linhaX = formataLinhaX(linhasParciais.length);
    const conteudo_ped = [...linhasParciais, linhaX].join("\n");

    let response_ftp;
    try {
      response_ftp = await enviar_ped_ftp(nome_ped, conteudo_ped, "SULPASSO");
    } catch (error) {
      console.error("Falha ao enviar para FTP: ", error);
      setFailAlertOpen(true);
      setLoadingCart(false);
      return;
    }

    // formata linhas para db
    const pedido_web_capa = montarObjetoPedido(sequenciaObtida, momento_pedido, visita_cliente);
    const pedido_web_itens = montarObjetoItem(sequenciaObtida, momento_pedido);

    let response_db;
    try{
      response_db = await enviar_ped_db(pedido_web_capa, pedido_web_itens);
    } catch (error){
      console.error("Falha ao salvar no banco de dados: ", error);
      setFailAlertOpen(true);
      setLoadingCart(false);
      return;
    }

    if (response_ftp.data.sucesso && response_db.data.sucesso) {
      clearCart();
      setObservacao("");
      setLoadingCart(false);
      setAlertOpen(true);
    } else {
      setLoadingCart(false);
      setFailAlertOpen(false);
    }
  };

  const formataNomeArquivo = (cod_repre, sequencia_pedido) => {
    const cod_repre_format = cod_repre.toString().padStart(4, "0");
    const sequencia_format = sequencia_pedido.toString().padStart(4, "0");

    return cod_repre_format + sequencia_format;
  };

  const formataLinhaP = (sequencia_pedido, dataHora, obs_representante) => {
    const obs_dia = "";
    const obs2 = "";
    const obs_web = observacao || "";
    const obs_rep = obs_representante || "";

    const TPD = "P";
    const PED = sequencia_pedido.toString().padStart(7, "0");
    const CLI = userData?.clI_CODIGO.toString().padStart(7, "0");
    const TAB = userData?.precO_TABELA.toString().padStart(2, "0");
    const NAT = userData?.natU_CODIGO.toString().padStart(3, "0"); 
    const DESD1 = userData?.conD_DESDOBRAMENTO01.toString();
    const DESD2 = userData?.conD_DESDOBRAMENTO02.toString();
    const DESD3 = userData?.conD_DESDOBRAMENTO03.toString();
    const DESD4 = userData?.conD_DESDOBRAMENTO04.toString();
    const DATA = obterDataFormatadaParaLinhaP(dataHora);
    const HORA = obterHoraAtual(dataHora);
    const DSC = "000000";
    const OBS_D = (obs_dia || "").toString().padEnd(25, " ").substring(0, 25);
    const TP_VDA = "PD";
    const OBDS = (obs_web + " | " + obs_rep).toString().padEnd(114, " ").substring(0, 114); 
    const OBDS2 = (obs2 || "").toString().padEnd(50, " ").substring(0, 50);
    const BCO = userData?.bancO_CODIGO.toString().padStart(3, "0"); 
    const LAT = "000000000000000";
    const LONG = "000000000000000";

    const linhaP =
      TPD +
      PED +
      CLI +
      TAB +
      NAT +
      DESD1 +
      DESD2 +
      DESD3 +
      DESD4 +
      DATA +
      HORA +
      DSC +
      OBS_D +
      TP_VDA +
      OBDS +
      OBDS2 +
      BCO +
      LAT +
      LONG;

    return linhaP;
  };

  const formataLinhaI = (sequencia_pedido) => {
    var list: string[] = [];

    cartItems.forEach((item) => {
      const TPD_i = "I";
      const PED = sequencia_pedido.toString().padStart(7, "0");
      const CLI = userData?.clI_CODIGO.toString().padStart(7, "0");
      const REF = item.codProduto.toString().padStart(6, "0").padEnd(10, " ");
      const QTD = item.quantidade_item.toString().padStart(4, "0");
      const PRC_D = item.preco.toString().replace(".", "").padStart(9, "0");
      const PRC_T = item.preco.toString().replace(".", "").padStart(9, "0");
      const DSC = "0".padStart(6, "0");
      const ICM = "0".padStart(3, "0");
      const ALIQ = "0".padStart(4, "0");
      const BASE = "0".padStart(9, "0");
      const AUTO_DSC = "0";
      const COD_P = item.codProduto.toString().padStart(6, "0");
      const DSC_I = "0".padStart(5, "0");
      const PRC_G = item.preco.toString().replace(".", "").padStart(9, "0");
      const TABLOID = "0";
      const MIN = "0".padStart(9, "0");
      const QTD_D = item.quantidade_item.toString().padStart(6, "0");

      const linhaI =
        TPD_i +
        PED +
        CLI +
        REF +
        QTD +
        PRC_D +
        PRC_T +
        DSC +
        ICM +
        ALIQ +
        BASE +
        AUTO_DSC +
        COD_P +
        DSC_I +
        PRC_G +
        TABLOID +
        MIN +
        QTD_D;

      list.push(linhaI);
    });

    return list;
  };

  const formataLinhaV = (sequencia_pedido, dataHora) => {
    const TPD_V = "V";
    const PED = sequencia_pedido.toString().padStart(7, "0");
    const CLI = userData?.clI_CODIGO.toString().padStart(7, "0");
    const MOT = "500";
    const DATA = obterDataFormatadaParaLinhaP(dataHora); // dd-MM-yyyy
    const HORA = obterHoraCompleta(dataHora); // hh:mm:ss
    const DIA = dataHora.getDay().toString().padStart(2, "0");
    const TP_VDA = "PD";
    const VENDA = "S";

    const linhaV = TPD_V + PED + CLI + MOT + DATA + HORA + DIA + TP_VDA + VENDA;

    return linhaV;
  };

  const formataLinhaX = (quantidade_linhas_ped) => {
    const TPD_X = "X";
    const PED = quantidade_linhas_ped.toString().padStart(3, "0");
    const linhaX = TPD_X + PED;
    return linhaX;
  };

  const montarObjetoPedido = (sequencia_pedido, dataHora, obs_representante) => {
    const obs_web = observacao || "";
    const obs_rep = obs_representante || "";

    const PED_capa = sequencia_pedido;
    const CLI_CODIGO_capa = parseInt(userData?.clI_CODIGO);
    const PRECO_TABELA_capa = parseInt(userData?.precO_TABELA);
    const NATU_CODIGO_capa = parseInt(userData?.natU_CODIGO);
    const DESD1_capa = parseInt(userData?.conD_DESDOBRAMENTO01);
    const DESD2_capa = parseInt(userData?.conD_DESDOBRAMENTO02);
    const DESD3_capa = parseInt(userData?.conD_DESDOBRAMENTO03);
    const DESD4_capa = parseInt(userData?.conD_DESDOBRAMENTO04);

    const OBS_capa = (obs_web + " | " + obs_rep).toString().substring(0, 114);
    const COD_BANCO_capa = parseInt(userData?.bancO_CODIGO);
    const EMPRE_CODIGO_capa = parseInt(userData?.emprE_CODIGO);

    return {
      COD_PEDIDO: PED_capa,
      CLI_CODIGO: CLI_CODIGO_capa,
      PRECO_TABELA: PRECO_TABELA_capa,
      NATU_CODIGO: NATU_CODIGO_capa,
      DESD1: DESD1_capa,
      DESD2: DESD2_capa,
      DESD3: DESD3_capa,
      DESD4: DESD4_capa,
      DATA: obterDataAtual(dataHora),
      HORA: obterHoraAtual(dataHora),
      OBS: OBS_capa,
      COD_BANCO: COD_BANCO_capa,
      EMPRE_CODIGO: EMPRE_CODIGO_capa,
    };
  };

  const montarObjetoItem = (sequencia_pedido, dataHora) => {
    return cartItems.map((item) => ({
      COD_PEDIDO: sequencia_pedido,
      CLI_CODIGO: userData?.clI_CODIGO,
      DATA: obterDataAtual(dataHora),
      HORA: obterHoraAtual(dataHora),
      COD_PROD: item.codProduto,
      QTD: item.quantidade_item,
      // PRC: item.preco,
      PRC: item.valorX,
      EMPRE_CODIGO: userData?.emprE_CODIGO,
      PROD_DESCRICAO: item.descricao,
      PROD_REF_FORN: item.referencia,
    }));
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: 9999, '& .MuiDrawer-paper': {height: '100%', width: 350, overflow: 'hidden'} }}>
        <Box display="flex" flexDirection="column" height='100%'>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <ShoppingBagOutlined />
              <Typography variant="subtitle1">
                {calcularQtdItens()} item{calcularQtdItens() !== 1 && "s "}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Divider />

          <Box flexGrow={1} overflow="auto" px={2} pb={2} sx={{ minHeight: 0 }}>
            {cartItems.length === 0 ? (
              <Box
                display="flex"
                flexDirection={"column"}
                alignItems="center"
                mt={15}
              >
                <ShoppingBagSharp sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h5">Seu carrinho está vazio.</Typography>
              </Box>
            ) : (
              cartItems.map((item, index) => (
                <Box key={index} display="flex" alignItems="center" mb={2}>
                  <img
                    src={item.image}
                    alt={item.image}
                    width={75}
                    height={75}
                    style={{ borderRadius: 8, marginRight: 16 }}
                  />
                  <Box flexGrow={1}>
                    <Typography fontWeight={500} mt={1}>
                      {item.descricao}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      R$ {item.valorX.toFixed(2)} x {item.quantidade_item}
                    </Typography>
                    <Box mt={1} display="flex" alignItems="center">
                      <IconButton
                        onClick={() => incrementQuantity(item.codProduto)}
                        size="small"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                      <Typography mx={1}>{item.quantidade_item}</Typography>
                      <IconButton
                        onClick={() => decrementQuantity(item.codProduto)}
                        size="small"
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <IconButton onClick={() => handleRemoveCart(item.codProduto)}>
                    <Close />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>

          <Divider />

          <Box p={2} bgcolor="dark" position="sticky" bottom={0}>
            <Stack spacing={1}>
              <FormControl>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <FormLabel htmlFor="observacao">OBSERVAÇÕES:</FormLabel>
                </Box>
                {cartItems.length === 0 ? (                
                <TextField
                  disabled
                  multiline
                  value={""}
                  rows={2}
                />) : (
                  <TextField
                  inputProps={{ maxLength: 50 }}
                  color="success"
                  multiline
                  rows={2}
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                />)}
              </FormControl>
              <React.Fragment>
                {cartItems.length === 0 ? (
                  <Button
                    disabled
                    variant="contained"
                    fullWidth
                    sx={{ fontWeight: "bold" }}
                    startIcon={<ShoppingCart />}
                  >
                    FINALIZAR COMPRA (R$ ${calcularTotal(cartItems)})
                  </Button>
                ) : (
                  <Button
                  onClick={() => {
                    onClose();  
                    setTimeout(() => { setSellerAlertOpen(true) }, 100);
                  }}
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ fontWeight: "bold", color: "#fff" }}
                    startIcon={!loadingCart ? <ShoppingCart /> : null}
                  >
                    {loadingCart ? (
                      <CircularProgress size="30px" />
                    ) : (
                      `FINALIZAR COMPRA (R$ ${calcularTotal(cartItems)})`
                    )}
                  </Button>
                )}

                {cartItems.length === 0 ? (
                  <Button
                    disabled
                    variant="contained"
                    fullWidth
                    sx={{ fontWeight: "bold", bgColor: "#000" }}
                  >
                    Limpar Carrinho
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ fontWeight: "bold", bgcolor: "#000" }}
                    onClick={() => clearCart()}
                  >
                    Limpar Carrinho
                  </Button>
                )}
              </React.Fragment>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <SellerAlert
        open={sellerAlertOpen}
        onClose={() => setSellerAlertOpen(false)}
        onCheckInResponse={(resposta) => {
          setSellerAlertOpen(false); 
          enviarPedido(resposta); 
        }}
      />

      <SuccessAlert open={alertOpen} onClose={() => setAlertOpen(false)} />
      <FailAlert open={failAlertOpen} onClose={() => setFailAlertOpen(false)}/>
    </>
  );
};

export default CartDrawer;
